import { useRef, useCallback, useState, useEffect } from 'react';
import type { Coordinate2D } from '../types';

/**
 * Configuration options for drag behavior
 */
export interface UseAttachmentDragOptions {
  /** Whether dragging is enabled */
  enabled?: boolean;
  /** Constraint dragging to vertical axis */
  constrainToVertical?: boolean;
  /** Minimum Y position */
  minY?: number;
  /** Maximum Y position */
  maxY?: number;
  /** Callback when drag starts */
  onDragStart?: (position: Coordinate2D) => void;
  /** Callback during drag */
  onDrag?: (position: Coordinate2D, delta: Coordinate2D) => void;
  /** Callback when drag ends */
  onDragEnd?: (position: Coordinate2D, totalDelta: Coordinate2D) => void;
}

/**
 * Drag state returned by the hook
 */
export interface AttachmentDragState {
  /** Whether currently dragging */
  isDragging: boolean;
  /** Current drag position */
  position: Coordinate2D;
  /** Delta from start position */
  delta: Coordinate2D;
  /** Mouse event handlers to attach to the draggable element */
  handlers: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
  };
}

/**
 * Hook for managing attachment dragging behavior
 */
export function useAttachmentDrag(
  initialPosition: Coordinate2D,
  options: UseAttachmentDragOptions = {}
): AttachmentDragState {
  const {
    enabled = true,
    constrainToVertical = true,
    minY,
    maxY,
    onDragStart,
    onDrag,
    onDragEnd,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<Coordinate2D>(initialPosition);
  const [delta, setDelta] = useState<Coordinate2D>({ x: 0, y: 0 });
  
  const dragStartRef = useRef<Coordinate2D>({ x: 0, y: 0 });
  const initialPositionRef = useRef<Coordinate2D>(initialPosition);

  // Update position when initialPosition changes
  useEffect(() => {
    if (!isDragging) {
      setPosition(initialPosition);
      initialPositionRef.current = initialPosition;
    }
  }, [initialPosition, isDragging]);

  // Get position from event
  const getEventPosition = useCallback((
    e: MouseEvent | TouchEvent
  ): Coordinate2D => {
    if ('touches' in e && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
    if ('clientX' in e) {
      return {
        x: e.clientX,
        y: e.clientY,
      };
    }
    return { x: 0, y: 0 };
  }, []);

  // Constrain position within bounds
  const constrainPosition = useCallback((pos: Coordinate2D): Coordinate2D => {
    let constrained = { ...pos };
    
    if (constrainToVertical) {
      constrained.x = initialPositionRef.current.x;
    }
    
    if (minY !== undefined && constrained.y < minY) {
      constrained.y = minY;
    }
    
    if (maxY !== undefined && constrained.y > maxY) {
      constrained.y = maxY;
    }
    
    return constrained;
  }, [constrainToVertical, minY, maxY]);

  // Handle drag move
  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging || !enabled) return;
    
    e.preventDefault();
    const currentPos = getEventPosition(e);
    
    const newDelta = {
      x: currentPos.x - dragStartRef.current.x,
      y: currentPos.y - dragStartRef.current.y,
    };
    
    const newPosition = constrainPosition({
      x: initialPositionRef.current.x + newDelta.x,
      y: initialPositionRef.current.y + newDelta.y,
    });
    
    const actualDelta = {
      x: newPosition.x - initialPositionRef.current.x,
      y: newPosition.y - initialPositionRef.current.y,
    };
    
    setPosition(newPosition);
    setDelta(actualDelta);
    onDrag?.(newPosition, actualDelta);
  }, [isDragging, enabled, getEventPosition, constrainPosition, onDrag]);

  // Handle drag end
  const handleDragEnd = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;
    
    e.preventDefault();
    setIsDragging(false);
    
    onDragEnd?.(position, delta);
  }, [isDragging, position, delta, onDragEnd]);

  // Set up global event listeners
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => handleDragMove(e);
      const handleMouseUp = (e: MouseEvent) => handleDragEnd(e);
      const handleTouchMove = (e: TouchEvent) => handleDragMove(e);
      const handleTouchEnd = (e: TouchEvent) => handleDragEnd(e);
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Handle drag start
  const handleDragStart = useCallback((
    e: React.MouseEvent | React.TouchEvent
  ) => {
    if (!enabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const startPos = 'touches' in e && e.touches.length > 0
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY };
    
    dragStartRef.current = startPos;
    initialPositionRef.current = position;
    
    setIsDragging(true);
    setDelta({ x: 0, y: 0 });
    
    onDragStart?.(position);
  }, [enabled, position, onDragStart]);

  // Event handlers
  const handlers = {
    onMouseDown: (e: React.MouseEvent) => handleDragStart(e),
    onTouchStart: (e: React.TouchEvent) => handleDragStart(e),
  };

  return {
    isDragging,
    position,
    delta,
    handlers,
  };
}